import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IFriendsStore, IFriend} from "data/stores/friends/friends.store";
import {RequestState, ModalType, ChecksumsKeys} from "data/enums";
import {AxiosError} from "axios";
import {extractErrorMessage} from "data/utils";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IChecksumStore} from "data/stores/checksum/checksum.store";
import type {IH2hStore} from "data/stores/h2h/h2h.store";
import type {IRosterStore} from "data/stores/roster/roster.store";

export interface IH2hFriendsController extends ViewController {
	i18n: ILocalizationStore;

	get isLoading(): boolean;

	get friends(): IFriend[];

	get friendInvites(): IFriend[];
}

@injectable()
export class H2hFriendsController implements IH2hFriendsController {
	@observable _requestState: RequestState = RequestState.IDLE;
	private _disposers: (() => void)[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.FriendsStore) public readonly _friendsStore: IFriendsStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) public readonly _userStore: IUserStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.RosterStore) public readonly _rosterStore: IRosterStore,
		@inject(Bindings.ChecksumStore) public readonly _checksums: IChecksumStore
	) {
		makeAutoObservable(this);
	}

	get isLoading() {
		return [RequestState.IDLE, RequestState.PENDING].includes(this._requestState);
	}

	get friends(): IFriend[] {
		return this._friendsStore.friends;
	}

	get friendInvites(): IFriend[] {
		return this._friendsStore.friendInvites;
	}

	init(param: void) {
		this._requestState = RequestState.PENDING;

		this._disposers = [
			this._checksums.addCallback(ChecksumsKeys.contestPlayerStats, this.fetchLive),
			this._checksums.addCallback(ChecksumsKeys.contests, this.fetchLive),
			this._checksums.addCallback(ChecksumsKeys.tournaments, this.fetchLive),
		];

		void Promise.all([this._friendsStore.fetch(), this._friendsStore.fetchInvites()])
			.then(this.onSuccess)
			.catch(this.onError);
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
		this._requestState = RequestState.IDLE;
	}

	private fetchLive = async () => {
		await this._friendsStore.fetch();
	};

	private onSuccess = () => {
		runInAction(() => {
			this._requestState = RequestState.SUCCESS;
		});
	};

	private onError = (error: AxiosError) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
		runInAction(() => {
			this._requestState = RequestState.ERROR;
		});
	};
}
